import React, { Component } from 'react';
import Store from 'stores/TariffsStore';
import {
  getTariffs,
  removeTariff,
  getTariffsUsage,
} from 'utils/TariffAPIUtils';
import paginate from 'utils/pagination-util';
import { findIndex } from 'lodash/array';
import * as Table from 'reactabular-table';
import * as sort from 'sortabular';
import * as Search from 'searchtabular';
import orderBy from 'lodash/orderBy';
import { OverlayTrigger, Tooltip, Pagination } from 'react-bootstrap';
import SessionStore from 'stores/SessionStore';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import Container from '../../v2/container';
import AddButton from '../../v2/buttons/AddButton';
import EditButton from '../../v2/buttons/EditButton';
import DeleteButton from '../../v2/buttons/DeleteButton';
import CopyButton from '../../v2/buttons/CopyButton';
import TariffEditorModal from './TariffEditorModal';
import TariffConfirmationButton from './TariffConfirmationButton';

const hasTariffRole = () => {
  return (
    SessionStore.getUserRoles().indexOf('tariff') > -1 ||
    SessionStore.getUserRoles().indexOf('admin') > -1
  );
};

const extendTariffsWithUsage = (tariffs, usage) => {
  return tariffs.map((tariff) => {
    tariff.numberOfShippers = usage[tariff._id] || 0;
    return tariff;
  });
};

const getStateFromStore = () => {
  let tariffs = Store.getTariffs();
  const usage = Store.getUsage();
  tariffs = extendTariffsWithUsage(tariffs, usage);
  return {
    tariffs: tariffs,
    usage: usage,
  };
};

class TariffsTable extends Component {
  constructor(props, context) {
    super(props, context);
    const getSortingColumns = () => this.state.sortingColumns || {};
    const sortable = sort.sort({
      getSortingColumns,
      onSort: (selectedColumn) => {
        this.setState((prevState) => ({
          sortingColumns: sort.byColumns({
            // sort.byColumn would work too
            sortingColumns: prevState.sortingColumns,
            selectedColumn,
          }),
        }));
      },
    });
    const { t } = props;

    this.state = {
      query: {},
      searchColumn: 'all',
      sortingColumns: null,
      pagination: {
        page: 0,
        perPage: 10,
      },
      columns: [
        {
          property: 'name',
          header: {
            label: t('NAME'),
            formatters: [
              sort.header({
                sortable,
                getSortingColumns,
                strategy: sort.strategies.byProperty,
              }),
            ],
          },
          cell: {
            formatters: [Search.highlightCell],
          },
        },
        {
          property: 'type',
          header: {
            label: t('TYPE'),
            formatters: [
              sort.header({
                sortable,
                getSortingColumns,
                strategy: sort.strategies.byProperty,
              }),
            ],
          },
          cell: {
            formatters: [Search.highlightCell],
          },
        },
        {
          property: 'markets',
          header: {
            label: titleize(t('MARKET', { count: 2 })),
            formatters: [
              sort.header({
                sortable,
                getSortingColumns,
                strategy: sort.strategies.byProperty,
              }),
            ],
          },
          cell: {
            formatters: [
              (value) => {
                return value.toString();
              },
              Search.highlightCell,
            ],
          },
        },
        {
          property: 'is_default',
          header: {
            label: t('DEFAULT_FOR_MARKET'),
            formatters: [
              sort.header({
                sortable,
                getSortingColumns,
                strategy: sort.strategies.byProperty,
              }),
            ],
          },
          cell: {
            formatters: [Search.highlightCell],
          },
        },
        {
          property: 'numberOfShippers',
          header: {
            label: t('OF_ASSIGNED_SHIPPERS'),
            formatters: [
              sort.header({
                sortable,
                getSortingColumns,
                strategy: sort.strategies.byProperty,
              }),
            ],
          },
          cell: {
            formatters: [Search.highlightCell],
          },
        },
        {
          header: {
            formatters: [
              () => hasTariffRole() && <AddButton onClick={this.newTariff} />,
            ],
          },
          cell: {
            formatters: [
              (value, { rowData }) => {
                const idx = findIndex(this.state.tariffs, {
                  _id: rowData._id,
                });
                let tariff = this.state.tariffs[idx];

                const remove = (password) => {
                  this.handleTariffDelete(tariff, password);
                };

                const edit = () => {
                  this.setState({ showEditor: true, tariff: tariff });
                };

                const clone = () => {
                  tariff = JSON.parse(JSON.stringify(tariff));
                  delete tariff._id;
                  tariff.name += '_copy';
                  this.setState({ showEditor: true, tariff: tariff });
                };
                const removeControl = (
                  <OverlayTrigger
                    placement="left"
                    overlay={
                      <Tooltip id={tariff._id}>{titleize(t('REMOVE'))}</Tooltip>
                    }
                  >
                    <div>
                      <DeleteButton />
                    </div>
                  </OverlayTrigger>
                );

                if (!hasTariffRole)
                  return <span>{t('ACTIONS_UNAVAILABLE')}</span>;
                return (
                  <div className="flex items-center space-x-2">
                    <TariffConfirmationButton
                      controlTitle={removeControl}
                      controlType="icon"
                      skipConfirmation={false}
                      alertTitle={t('WARNING')}
                      message={t('IMPACT_CUSTOMERS_MSG')}
                      confirmTitle={t('CONFIRM_REMOVE')}
                      dismissTitle={titleize(t('CANCEL'))}
                      onConfirm={remove.bind(this)}
                      store={Store}
                    />
                    &nbsp;
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={tariff._id}>{t('EDIT')}</Tooltip>}
                    >
                      <div>
                        <EditButton
                          style={{ cursor: 'pointer', color: 'green' }}
                          onClick={edit.bind(this)}
                        />
                      </div>
                    </OverlayTrigger>
                    &nbsp;
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip id={tariff._id}>
                          {titleize(t('CLONE'))}
                        </Tooltip>
                      }
                    >
                      <CopyButton onClick={clone.bind(this)} />
                    </OverlayTrigger>
                  </div>
                );
              },
            ],
          },
        },
      ],
    };
  }

  componentDidMount() {
    Store.addChangeListener(this.onChange);
    getTariffsUsage();
    getTariffs();
  }

  componentWillUnmount() {
    Store.removeChangeListener(this.onChange);
  }

  onChange = () => {
    this.setState(getStateFromStore());
  };

  newTariff = () => {
    this.setState({ showEditor: true });
  };

  handleTariffDelete = (tariff, password) => {
    if (tariff) {
      Store.reset();
      removeTariff(tariff, password);
    }
  };

  onSelect = (eventKey) => {
    this.setState((prevState) => {
      let pagination = prevState.pagination || {};
      pagination.page = eventKey - 1;
      return { pagination };
    });
  };

  onPerPage = (e) => {
    const { value } = e.target;
    this.setState((prevState) => ({
      pagination: {
        ...(prevState.pagination || {}),
        perPage: parseInt(value, 10),
      },
    }));
  };

  render() {
    const { t } = this.props;
    let tariffs = this.state.tariffs;
    const columns = this.state.columns;
    const searchColumn = this.state.searchColumn;
    const query = this.state.query;
    const pagination = this.state.pagination;
    const tariff = this.state.tariff;
    const showEditor = this.state.showEditor;
    const sortingColumns = this.state.sortingColumns;
    if (tariffs && this.state.query) {
      tariffs = Search.highlighter({
        columns: columns,
        matches: Search.matches,
        query,
      })(Search.multipleColumns({ columns, query })(tariffs));
    }
    tariffs = sort.sorter({
      columns: columns,
      sortingColumns,
      sort: orderBy,
    })(tariffs);
    const paginated = paginate(tariffs, pagination);
    let totalPages = 0;
    if (pagination.perPage && tariffs) {
      totalPages = Math.ceil(tariffs.length / pagination.perPage);
    }
    const close = () => {
      this.setState({ showEditor: false, tariff: null });
    };
    let modal = <div />;
    if (showEditor) {
      modal = (
        <TariffEditorModal
          tariffsList={this.state.tariffs}
          className="tariff-wrapper"
          tariff={tariff}
          onClose={close.bind(this)}
          show
        />
      );
    }
    const paginationLabel = t('PAGE_CURRENT_TOTAL', {
      current: pagination.page + 1,
      total: totalPages,
    });
    return (
      <Container>
        <div className="mt-10 tariff-wrapper">
          <h1 className="my-0 mb-4 text-xl">{t('TARIFFS')}</h1>
          <div className="mb-2 w-max">
            <Search.Field
              column={searchColumn}
              query={query}
              columns={columns}
              rows={paginated.data}
              className="flex items-center form-group"
              i18n={{ all: titleize(t('ALL')) }}
              components={{
                props: {
                  select: {
                    className: 'form-select',
                  },
                  filter: {
                    className: 'ml-2 form-field',
                    placeholder: titleize(t('SEARCH')),
                  },
                },
              }}
              onColumnChange={(selectedColumn) => {
                this.setState({ searchColumn: selectedColumn });
              }}
              onChange={(currentQuery) => {
                this.setState({ query: currentQuery });
              }}
            />
          </div>

          <div className="overflow-x-auto shadow rounded-md">
            <Table.Provider className="default-table" columns={columns}>
              <Table.Header />
              <Table.Body rows={paginated.data} rowKey="_id" />
            </Table.Provider>
          </div>
          <div className="flex flex-wrap items-center justify-between">
            <Pagination
              prev
              next
              first
              last
              ellipsis
              items={paginated.amount}
              maxButtons={10}
              activePage={pagination.page + 1}
              label={t('PAGE')}
              onSelect={(eventKey) => this.onSelect(eventKey)}
            />
            <div className="flex items-center -mx-8 wrap">
              <div className="px-8">
                <div className="flex items-center">
                  <label className="mr-2 form-label">{t('PER_PAGE')}</label>
                  <input
                    type="number"
                    className="w-20 form-field"
                    value={this.state.pagination.perPage}
                    onChange={this.onPerPage}
                  />
                </div>
              </div>
              <h4 className="px-8 my-0">{paginationLabel}</h4>
            </div>
          </div>
          <div>{modal}</div>
        </div>
      </Container>
    );
  }
}

export default withTranslation()(TariffsTable);
